import React, { useEffect, useState, useMemo } from "react";
import { Button, Layout, message, Modal, Spin } from "antd";
import { useMutation, useQuery } from "@apollo/client";
import { Role_Management, Role_Account_Query } from "../../../services/userAPI";
import "../../Dashboard/viewRole.css";
import Clan from "../../../Images/clan-logo.png";
import Darts from "../../../Images/Darts-Logo 1.png";
import Lense from "../../../Images/Lense-Logo 1.png";
import { useLocation, useNavigate } from "react-router-dom";

const { Content } = Layout;

const AdminViewRole = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // Parse IDs safely
    const roleId = location.state?.roleId ? parseInt(location.state.roleId) : null;
    const accountId = location.state?.accountId ? parseInt(location.state.accountId) : null;
    const accountName = location.state?.accountName || "Unknown Account";
    const roleName = location.state?.roleName || location.state?.name; // fallback from dashboard
    const clientName = location.state?.clientName || "—";
    const clientUrl = location.state?.clientUrl || "";
    const clientAddress = location.state?.clientAddress || "";
    const clientAddress2 = location.state?.clientAddress2 || "";
    const clientBrandColor = location.state?.clientBrandColor || "D00000";
    const name = location.state?.name || "";

    // Validate required IDs
    useEffect(() => {
        if (!accountId || !roleId) {
            message.error("Invalid role or account. Redirecting...");
            navigate("/admin-home", { replace: true });
        }
    }, [accountId, roleId, navigate]);

    // State for product access
    const [productAccess, setProductAccess] = useState({
        DARTS: location.state?.dartsNeeded ?? false,
        CLAN: location.state?.clanNeeded ?? false,
        LENSE: location.state?.lenseNeeded ?? false,
    });
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showAccessModal, setShowAccessModal] = useState(false);
    const [updatingProduct, setUpdatingProduct] = useState(null);

    // Fetch job role data
    const { loading: roleLoading, error: roleError, data: roleData, refetch } = useQuery(Role_Account_Query, {
        variables: { accountId },
        fetchPolicy: "network-only",
        skip: !accountId,
    });

    // Find the selected role from API data
    const selectedRole = useMemo(() => {
        if (!roleData?.getAllJobrole || !roleId) return null;
        return roleData.getAllJobrole.find(role => role.id === roleId);
    }, [roleData, roleId]);

    // Refetch on mount
    useEffect(() => {
        if (accountId && roleId) {
            refetch();
        }
    }, [accountId, roleId, refetch]);

    // Update productAccess when roleData loads
    useEffect(() => {
        if (selectedRole) {
            setProductAccess({
                DARTS: selectedRole.dartsNeeded ?? false,
                CLAN: selectedRole.clanNeeded ?? true,
                SYNC: selectedRole.syncNeeded ?? false,
                LENSE: selectedRole.lenseNeeded ?? false,
            });
        }
    }, [selectedRole]);

    const [addJobrole, { loading: addRoleLoading }] = useMutation(Role_Management);

    const handleUpdateAccess = async () => {
        if (!selectedRole) {
            message.error("Role data not available.");
            return;
        }

        const newAccess = !productAccess[selectedProduct];
        setProductAccess(prev => ({ ...prev, [selectedProduct]: newAccess }));
        setUpdatingProduct(selectedProduct);
        setShowAccessModal(false);

        const variables = {
            accountId,
            jobroleId: roleId,
            name: selectedRole.name,

            clanNeeded: selectedProduct === "CLAN" ? newAccess : selectedRole.clanNeeded,
            syncNeeded: selectedProduct === "SYNC" ? newAccess : selectedRole.syncNeeded,
            dartsNeeded: selectedProduct === "DARTS" ? newAccess : selectedRole.dartsNeeded,
            lenseNeeded: selectedProduct === "LENSE" ? newAccess : selectedRole.lenseNeeded,

            dartsFormNeeded: selectedRole.dartsFormNeeded,
            pocName: selectedRole.pocName || "",
            pocEmail: selectedRole.pocEmail || "",
            pocNumber: selectedRole.pocContactNumber || "",
            numberLogin: selectedRole.numberLogin,
            emailLogin: selectedRole.emailLogin,
            mandatoryTimeDistribution: selectedRole.mandatoryTimeDistribution,
            signInSignOutRequired: selectedRole.signInSignOutRequired,
            locationRequired: selectedRole.locationRequired,
            targetEditDays: selectedRole.targetEditDays,
            inputEditDays: selectedRole.inputEditDays,
            ldmsNeeded: selectedRole.ldmsNeeded,
            mirrorNeeded: selectedRole.mirrorNeeded,
            geoTracking: selectedRole.geoTracking ?? false,
            jobroleAccessUserIds: selectedRole.jobroleAccessUsers?.map(u => u.id) || [],
        };

        try {
            await addJobrole({ variables });
            message.success(`${selectedProduct} access updated`);
            await refetch();
        } catch (err) {
            setProductAccess(prev => ({ ...prev, [selectedProduct]: !newAccess }));
            message.error("Update failed: " + err.message);
        } finally {
            setUpdatingProduct(null);
        }
    };

    // Base product definitions
    const allProducts = [
        { name: "LENSE", tagline: "DISCOVER THE UNSEEN", logo: Lense, route: "/lense-management" },
        { name: "DARTS", tagline: "MAKE EVERYDAY COUNT", logo: Darts, route: "/admin-product-configuration" },
        { name: "CLAN", tagline: "WIN AT WORK", logo: Clan, route: "/admin-clan-management" },
    ];

    // Filter enabled products
    const allowedProducts = useMemo(() => {
        if (!selectedRole) return [];
        return allProducts.filter(p => {
            if (p.name === "LENSE") return selectedRole.lenseNeeded;
            if (p.name === "DARTS") return selectedRole.dartsNeeded;
            if (p.name === "CLAN") return selectedRole.clanNeeded;
            if (p.name === "SYNC") return selectedRole.syncNeeded;
            return false;
        });
    }, [selectedRole]);

    const handleProductClick = (product) => {
        navigate(product.route, {
            state: {
                roleId,
                accountId,
                accountName,
                roleName: selectedRole?.name || roleName,
                dartsFormNeeded: selectedRole?.dartsFormNeeded ?? false,
                pocName: selectedRole?.pocName || "",
                pocEmail: selectedRole?.pocEmail || "",
                pocNumber: selectedRole?.pocContactNumber || "",
                numberLogin: selectedRole?.numberLogin ?? false,
                emailLogin: selectedRole?.emailLogin ?? false,
                mandatoryTimeDistribution: selectedRole?.mandatoryTimeDistribution ?? false,
                targetEditDays: selectedRole?.targetEditDays ?? 0,
                inputEditDays: selectedRole?.inputEditDays ?? 0,
                dartsNeeded: productAccess.DARTS,
                clanNeeded: productAccess.CLAN,
                syncNeeded: productAccess.SYNC,
                lenseNeeded: productAccess.LENSE,
                name,
            },
        });
    };

    const handleDiscardClick = () => {
        navigate("/admin-home");
    };

    const getLoginType = (role) => {
        if (role.emailLogin && role.numberLogin) return "Email / Number";
        if (role.emailLogin) return "Email";
        if (role.numberLogin) return "Number";
        return "None";
    };

    const yesNo = (value) => (value ? "Yes" : "No");

    return (
        <div className="user-profile-container">

            <div className="product-selection-container">
                {/* Breadcrumb */}
                <div className="breadcrumbAndHome">
                    <span onClick={() => navigate("/admin-home")} className="bold-text">
                        {accountName} <span style={{ fontWeight: "bold" }}>&gt; </span>
                        {selectedRole?.name || roleName || "Loading..."}
                    </span>
                    <button className="back-button" onClick={handleDiscardClick}>
                        Back to Home
                    </button>
                </div>

                {/* Loading State */}
                {roleLoading && (
                    <div className="text-center" style={{ padding: "60px 20px" }}>
                        <Spin size="large" />
                        <div style={{ marginTop: "16px", color: "#666" }}>Loading role details...</div>
                    </div>
                )}

                {/* Error State */}
                {roleError && (
                    <div className="text-center text-red-600" style={{ padding: "60px 20px" }}>
                        Error loading role: {roleError.message}
                        <div style={{ marginTop: "16px" }}>
                            <Button onClick={() => refetch()}>Retry</Button>
                        </div>
                    </div>
                )}

                {/* Role Not Found */}
                {!roleLoading && !selectedRole && roleId && (
                    <div className="text-center text-gray-500" style={{ padding: "60px 20px" }}>
                        Role not found.
                    </div>
                )}

                {/* === MAIN CONTENT: ONLY IF selectedRole EXISTS === */}
                {!roleLoading && selectedRole && (
                    <>
                        {/* THREE-CARD GRID */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 mt-2">

                            {/* 1. ROLE MANAGEMENT */}
                            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900 border-b-2 border-red-600 pb-2 mb-5">
                                    Role Management
                                </h3>
                                <div className="space-y-4 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 font-medium">Role Name</span>
                                        <span className="text-gray-900">{selectedRole.name}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 font-medium">Account</span>
                                        <span className="text-gray-900">{accountName}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 font-medium">POC Name</span>
                                        <span className="text-gray-900">{selectedRole.pocName || "—"}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 font-medium">POC Email</span>
                                        <span className="text-gray-900">{selectedRole.pocEmail || "—"}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 font-medium">POC Number</span>
                                        <span className="text-gray-900">{selectedRole.pocContactNumber || "—"}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 font-medium">Login Type</span>
                                        <span className="text-gray-600">
                                            {getLoginType(selectedRole)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* 2. CLIENT PROFILE */}
                            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900 border-b-2 border-red-600 pb-2 mb-5">
                                    Client Profile
                                </h3>
                                <div className="space-y-4 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 font-medium">Client Name</span>
                                        <span className="text-gray-900">{clientName}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 font-medium">Website</span>
                                        <span className="text-gray-900 truncate max-w-[140px]">
                                            {clientUrl || "—"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 font-medium">Address Line 1</span>
                                        <span className="text-gray-900 text-right max-w-[140px] break-words">
                                            {clientAddress || "—"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 font-medium">Address Line 2</span>
                                        <span className="text-gray-900 text-right max-w-[140px] break-words">
                                            {clientAddress2 || "—"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-600 font-medium">Brand Color</span>
                                        <div className="flex items-center gap-2">
                                            <div
                                                className="w-6 h-6 rounded-full border border-gray-300"
                                                style={{ backgroundColor: `#${clientBrandColor}` }}
                                            />
                                            <span className="text-gray-900">#{clientBrandColor}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Product Selection */}
                        <h2 className="title">Select The Product To Configure</h2>

                        {allowedProducts.length === 0 ? (
                            <p className="text-center text-gray-500" style={{ padding: "40px 20px" }}>
                                No products are enabled for this role.
                            </p>
                        ) : (
                            <div className="product-grid-admin">
                                {allowedProducts.map((product, index) => (
                                    <div
                                        key={index}
                                        className={`product-card ${updatingProduct === product.name ? 'updating' : 'active'}`}
                                        onClick={() => handleProductClick(product)}
                                        style={{ cursor: "pointer", position: "relative" }}
                                    >
                                        {updatingProduct === product.name && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    zIndex: 10,
                                                }}
                                            >
                                                <Spin size="small" />
                                            </div>
                                        )}
                                        <img src={product.logo} alt={product.name} className="product-logo" />
                                    </div>
                                ))}
                            </div>
                        )}

                        <br />

                        {/* User Management Button */}
                        <div className="user-management-and-role-management-header">
                            <button
                                className="userManagementRedirect"
                                onClick={() =>
                                    navigate("/admin-user-management", {
                                        state: {
                                            accountId,
                                            roleId,
                                            accountName,
                                            roleName: selectedRole.name,
                                            name,
                                        },
                                    })
                                }
                            >
                                User Management
                            </button>
                            <button
                                className="userManagementRedirect"
                                onClick={() =>
                                    navigate("/admin-faq", {
                                        state: {
                                            accountId,
                                            roleId,
                                            accountName,
                                            name: selectedRole.name,
                                        },
                                    })
                                }
                            >
                                FAQ's
                            </button>
                            <div className="user-management-and-role-management-header">
                                <button
                                    className="faqRedirect"
                                    onClick={() => navigate("/mark-holidays", {
                                        state: { accountId, roleId, accountName, name }
                                    })}
                                >
                                    Mark Holidays
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {/* Access Modal */}
                <Modal
                    title="Access Required"
                    open={showAccessModal}
                    onCancel={() => setShowAccessModal(false)}
                    footer={[
                        <button
                            key="no"
                            onClick={() => setShowAccessModal(false)}
                            className="cancel-button"
                            style={{ marginRight: "10px" }}
                        >
                            No
                        </button>,
                        <button
                            className="create-button"
                            key="yes"
                            type="primary"
                            onClick={handleUpdateAccess}
                            disabled={addRoleLoading}
                        >
                            {addRoleLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : "Yes"}
                        </button>,
                    ]}
                >
                    <p>Do you need to give access to {selectedProduct}?</p>
                </Modal>

            </div>
        </div>
    );
};

export default AdminViewRole;